// Base DAO — shared between FE (Vite) and edge functions (Deno).
// FE resolves @supabase/supabase-js from node_modules.
// Deno resolves it via import map in supabase/functions/deno.json.

import type { SupabaseClient } from '@supabase/supabase-js';

export type { SupabaseClient };

// ── Invalidation ────────────────────────────────────────────────────────

/** Refine-compatible invalidation function signature. */
export type InvalidateFn = (params: {
  resource: string;
  invalidates: ('list' | 'detail' | 'resourceAll')[];
}) => void;

/** Options for mutation methods. */
export interface MutationOptions {
  /**
   * Control cache invalidation after mutation:
   * - undefined / omitted → invalidate all dependentResources (default)
   * - string[]            → invalidate only these specific resources
   * - false               → skip invalidation (useful for batches — call invalidateAll() manually)
   */
  invalidate?: string[] | false;
}

// ── BaseDao ─────────────────────────────────────────────────────────────

export abstract class BaseDao<T> {
  /** All resources that depend on this entity (tables, views, other entities). */
  protected abstract readonly dependentResources: string[];

  /** Override in subclass for default joins/columns. Defaults to '*'. */
  protected readonly defaultSelect: string = '*';

  constructor(
    protected readonly sb: SupabaseClient,
    protected readonly table: string,
    private readonly invalidateFn?: InvalidateFn,
  ) {}

  // --- Generic CRUD (all tables use bigint IDs) ---

  async getById(id: number): Promise<T> {
    const { data, error } = await this.sb
      .from(this.table)
      .select(this.defaultSelect)
      .eq('id', id)
      .single();
    if (error) throw error;
    return data as T;
  }

  async create(input: Partial<T>, opts?: MutationOptions): Promise<T> {
    const { data, error } = await this.sb
      .from(this.table)
      .insert(input as Record<string, unknown>)
      .select(this.defaultSelect)
      .single();
    if (error) throw error;
    this.handleInvalidation(opts);
    return data as T;
  }

  async update(id: number, input: Partial<T>, opts?: MutationOptions): Promise<T> {
    const { data, error } = await this.sb
      .from(this.table)
      .update(input as Record<string, unknown>)
      .eq('id', id)
      .select(this.defaultSelect)
      .single();
    if (error) throw error;
    this.handleInvalidation(opts);
    return data as T;
  }

  /** Batch update rows by ID. Parallel PATCH calls (upsert doesn't work with partial rows + GENERATED ALWAYS id). */
  async updateMany(rows: Array<Partial<T> & { id: number }>, opts?: MutationOptions): Promise<void> {
    if (rows.length === 0) return;
    await Promise.all(rows.map(({ id, ...fields }) =>
      this.sb.from(this.table).update(fields as Record<string, unknown>).eq('id', id).then(({ error }) => { if (error) throw error; }),
    ));
    this.handleInvalidation(opts);
  }

  async delete(id: number, opts?: MutationOptions): Promise<void> {
    const { error } = await this.sb
      .from(this.table)
      .delete()
      .eq('id', id);
    if (error) throw error;
    this.handleInvalidation(opts);
  }

  // --- Invalidation ---

  /** Invalidate all dependentResources. Call manually after batch operations. */
  invalidateAll(): void {
    this.invalidateResources(this.dependentResources);
  }

  /** Invalidate a specific subset of resources. */
  protected invalidateResources(resources: string[]): void {
    if (!this.invalidateFn) return;
    for (const resource of resources) {
      this.invalidateFn({ resource, invalidates: ['resourceAll'] });
    }
  }

  protected handleInvalidation(opts?: MutationOptions): void {
    if (!this.invalidateFn) return;
    if (opts?.invalidate === false) return;
    if (Array.isArray(opts?.invalidate)) {
      this.invalidateResources(opts.invalidate);
    } else {
      this.invalidateAll();
    }
  }
}
